/* ==========================================================================
   Sound Event Detector - Legal Document PDF Exporter
   High-Fidelity Client-Side PDF Generation & Download for all 9 Legal Documents
   Languages Supported: CS, EN, DE, ES, PT, AR, IT
   Features:
   - Clean white header with official JALUD Embedded logo and company details
   - Smart section-aware page slicing preventing clipped text cards
   - Pure canvas-drawn running headers & footers with full Unicode support (no font corruption)
   - Guaranteed top, bottom, and side margins on every page
   - Zero-overlap running header with dynamic text truncation and exact coordinate spacing
   ========================================================================== */

import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import defaultJsPDF from 'jspdf';
import { legalTranslations, LEGAL_VERSIONS } from '../locales/legalTranslations.js';

/**
 * Resilient constructor resolver for jsPDF across ESM / CJS / Vite bundling
 */
function createPdfInstance(orientation = 'p', unit = 'mm', format = 'a4') {
  if (typeof jsPDF === 'function') {
    return new jsPDF(orientation, unit, format);
  }
  if (typeof defaultJsPDF === 'function') {
    return new defaultJsPDF(orientation, unit, format);
  }
  if (defaultJsPDF && typeof defaultJsPDF.jsPDF === 'function') {
    return new defaultJsPDF.jsPDF(orientation, unit, format);
  }
  if (typeof window !== 'undefined' && window.jspdf && typeof window.jspdf.jsPDF === 'function') {
    return new window.jspdf.jsPDF(orientation, unit, format);
  }
  throw new Error('jsPDF constructor not found');
}

// Official JALUD Embedded Logo (High Resolution Base64 PNG)
const JALUD_LOGO_BASE64 = 'data:image/png;base64,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';

// Doc Key to Translation Object Mapping
const DOC_DATA_MAP = {
  eula: 'eula',
  'eula-page': 'eula',
  terms: 'terms',
  'terms-and-conditions': 'terms',
  privacy: 'privacy',
  'privacy-policy': 'privacy',
  productPrivacy: 'productPrivacy',
  'product-privacy': 'productPrivacy',
  dpa: 'dpa',
  'data-processing-agreement': 'dpa',
  support: 'support',
  'support-and-maintenance': 'support',
  ethics: 'ethics',
  'code-of-ethics': 'ethics',
  ims: 'ims',
  'policy-ims': 'ims',
  cookie: 'cookiePolicy',
  cookiePolicy: 'cookiePolicy',
  'cookie-policy': 'cookiePolicy'
};

export const PDF_BUTTON_LABELS = {
  cs: 'Stáhnout PDF',
  en: 'Download PDF',
  de: 'PDF herunterladen',
  es: 'Descargar PDF',
  pt: 'Descarregar PDF',
  ar: 'تنزيل PDF',
  it: 'Scarica PDF'
};

const I18N_LABELS = {
  cs: {
    loading: 'Generuji PDF...',
    downloaded: '✓ PDF staženo',
    officialDoc: 'Oficiální právní dokumentace',
    generatedOn: 'Vygenerováno dne',
    pageOf: 'Strana',
    toc: 'Obsah dokumentu',
    companyNotice: 'JALUD Embedded s.r.o. — Všechna práva vyhrazena'
  },
  en: {
    loading: 'Generating PDF...',
    downloaded: '✓ PDF Downloaded',
    officialDoc: 'Official Legal Documentation',
    generatedOn: 'Generated on',
    pageOf: 'Page',
    toc: 'Table of Contents',
    companyNotice: 'JALUD Embedded s.r.o. — All rights reserved'
  },
  de: {
    loading: 'PDF wird generiert...',
    downloaded: '✓ PDF heruntergeladen',
    officialDoc: 'Offizielle Rechtsdokumentation',
    generatedOn: 'Erstellt am',
    pageOf: 'Seite',
    toc: 'Inhaltsverzeichnis',
    companyNotice: 'JALUD Embedded s.r.o. — Alle Rechte vorbehalten'
  },
  es: {
    loading: 'Generando PDF...',
    downloaded: '✓ PDF descargado',
    officialDoc: 'Documentación Legal Oficial',
    generatedOn: 'Generado el',
    pageOf: 'Página',
    toc: 'Índice de Contenidos',
    companyNotice: 'JALUD Embedded s.r.o. — Todos los derechos reservados'
  },
  pt: {
    loading: 'A gerar PDF...',
    downloaded: '✓ PDF descarregado',
    officialDoc: 'Documentação Jurídica Oficial',
    generatedOn: 'Gerado a',
    pageOf: 'Página',
    toc: 'Índice',
    companyNotice: 'JALUD Embedded s.r.o. — Todos os direitos reservados'
  },
  ar: {
    loading: 'جاري إنشاء ملف PDF...',
    downloaded: '✓ تم تنزيل PDF',
    officialDoc: 'وثيقة قانونية رسمية',
    generatedOn: 'تم الإنشاء في',
    pageOf: 'صفحة',
    toc: 'فهرس المحتويات',
    companyNotice: 'JALUD Embedded s.r.o. — جميع الحقوق محفوظة'
  },
  it: {
    loading: 'Generazione PDF in corso...',
    downloaded: '✓ PDF scaricato',
    officialDoc: 'Documentazione Legale Ufficiale',
    generatedOn: 'Generato il',
    pageOf: 'Pagina',
    toc: 'Indice del documento',
    companyNotice: 'JALUD Embedded s.r.o. — Tutti i diritti riservati'
  }
};

/**
 * Builds clean HTML representation of legal document for A4 PDF export
 */
function buildDocumentHtml(docKey, lang, data) {
  const isRtl = lang === 'ar';
  const labels = I18N_LABELS[lang] || I18N_LABELS.en;
  const today = new Date().toLocaleDateString(lang === 'cs' ? 'cs-CZ' : lang === 'it' ? 'it-IT' : 'en-GB');
  const versionText = data.version || (LEGAL_VERSIONS && LEGAL_VERSIONS[lang]) || 'Verze 2026.1 (Účinnost od 1. ledna 2026)';

  const isCookiePolicy = docKey === 'cookie' || docKey === 'cookiePolicy' || docKey === 'cookie-policy';
  let bodyHtml = '';

  if (isCookiePolicy) {
    bodyHtml = `
      <div style="display: flex; flex-direction: column; gap: 14px;">
        <div class="pdf-section" style="border-bottom: 1px solid #e2e8f0; padding-bottom: 12px; margin-bottom: 6px;">
          <h2 style="font-size: 13.5px; font-weight: 800; color: #0f172a; margin: 0 0 6px;">1. ${data.sec1Title || ''}</h2>
          <p style="font-size: 11px; line-height: 1.6; color: #334155; margin: 0 0 6px;">${data.sec1P1 || ''}</p>
          <p style="font-size: 11px; line-height: 1.6; color: #334155; margin: 0;">${data.sec1P2 || ''}</p>
        </div>

        <div class="pdf-section" style="border-bottom: 1px solid #e2e8f0; padding-bottom: 12px; margin-bottom: 6px;">
          <h2 style="font-size: 13.5px; font-weight: 800; color: #0f172a; margin: 0 0 8px;">2. ${data.sec2Title || ''}</h2>
          <div style="margin-bottom: 6px; padding: 8px 12px; background: #f8fafc; border-left: 3px solid #0066cc; border-radius: 4px;">
            <strong style="font-size: 11px; color: #0f172a;">${data.catATitle || ''}</strong>
            <p style="font-size: 10.5px; line-height: 1.5; color: #475569; margin: 3px 0 0;">${data.catADesc || ''}</p>
          </div>
          <div style="margin-bottom: 6px; padding: 8px 12px; background: #f8fafc; border-left: 3px solid #0066cc; border-radius: 4px;">
            <strong style="font-size: 11px; color: #0f172a;">${data.catBTitle || ''}</strong>
            <p style="font-size: 10.5px; line-height: 1.5; color: #475569; margin: 3px 0 0;">${data.catBDesc || ''}</p>
          </div>
          <div style="padding: 8px 12px; background: #f8fafc; border-left: 3px solid #0066cc; border-radius: 4px;">
            <strong style="font-size: 11px; color: #0f172a;">${data.catCTitle || ''}</strong>
            <p style="font-size: 10.5px; line-height: 1.5; color: #475569; margin: 3px 0 0;">${data.catCDesc || ''}</p>
          </div>
        </div>

        <div class="pdf-section" style="border-bottom: 1px solid #e2e8f0; padding-bottom: 12px; margin-bottom: 6px;">
          <h2 style="font-size: 13.5px; font-weight: 800; color: #0f172a; margin: 0 0 8px;">3. ${data.sec3Title || ''}</h2>
          <p style="font-size: 11px; line-height: 1.5; color: #475569; margin: 0 0 10px;">${data.sec3Intro || ''}</p>
          
          <table style="width: 100%; border-collapse: collapse; font-size: 10px; margin-bottom: 6px;">
            <thead>
              <tr style="background: #f1f5f9; border-bottom: 1.5px solid #cbd5e1; text-align: ${isRtl ? 'right' : 'left'};">
                <th style="padding: 6px 8px;">${data.thName || 'Name'}</th>
                <th style="padding: 6px 8px;">${data.thProvider || 'Provider'}</th>
                <th style="padding: 6px 8px;">${data.thStorageType || 'Type'}</th>
                <th style="padding: 6px 8px;">${data.thCategory || 'Category'}</th>
                <th style="padding: 6px 8px;">${data.thDuration || 'Duration'}</th>
              </tr>
            </thead>
            <tbody>
              <tr style="border-bottom: 1px solid #e2e8f0;">
                <td style="padding: 6px 8px; font-family: monospace; font-weight: 700;">sed_cookie_consent_v2</td>
                <td style="padding: 6px 8px;">JALUD Embedded</td>
                <td style="padding: 6px 8px;">LocalStorage</td>
                <td style="padding: 6px 8px;"><span style="background: #e0f2fe; color: #0369a1; padding: 2px 6px; border-radius: 4px;">${data.catBadgeNecessary || 'Necessary'}</span></td>
                <td style="padding: 6px 8px;">${data.consentDuration || 'Persistent'}</td>
              </tr>
              <tr style="border-bottom: 1px solid #e2e8f0;">
                <td style="padding: 6px 8px; font-family: monospace; font-weight: 700;">_ga</td>
                <td style="padding: 6px 8px;">Google LLC</td>
                <td style="padding: 6px 8px;">HTTP Cookie</td>
                <td style="padding: 6px 8px;"><span style="background: #fef3c7; color: #92400e; padding: 2px 6px; border-radius: 4px;">${data.catBadgeAnalytics || 'Analytics'}</span></td>
                <td style="padding: 6px 8px;">${data.gaDuration || '2 years'}</td>
              </tr>
              <tr style="border-bottom: 1px solid #e2e8f0;">
                <td style="padding: 6px 8px; font-family: monospace; font-weight: 700;">_ga_*</td>
                <td style="padding: 6px 8px;">Google LLC</td>
                <td style="padding: 6px 8px;">HTTP Cookie</td>
                <td style="padding: 6px 8px;"><span style="background: #fef3c7; color: #92400e; padding: 2px 6px; border-radius: 4px;">${data.catBadgeAnalytics || 'Analytics'}</span></td>
                <td style="padding: 6px 8px;">${data.ga4Duration || '2 years'}</td>
              </tr>
              <tr style="border-bottom: 1px solid #e2e8f0;">
                <td style="padding: 6px 8px; font-family: monospace; font-weight: 700;">__hstc, hubspotutk, __hssc, __hssrc</td>
                <td style="padding: 6px 8px;">HubSpot, Inc.</td>
                <td style="padding: 6px 8px;">HTTP Cookie</td>
                <td style="padding: 6px 8px;"><span style="background: #fef3c7; color: #92400e; padding: 2px 6px; border-radius: 4px;">${data.catBadgeAnalytics || 'Analytics'}</span></td>
                <td style="padding: 6px 8px;">${data.hsDuration || 'Session to 13 months'}</td>
              </tr>
              <tr style="border-bottom: 1px solid #e2e8f0;">
                <td style="padding: 6px 8px; font-family: monospace; font-weight: 700;">YSC, PREF, GPS</td>
                <td style="padding: 6px 8px;">YouTube / Google</td>
                <td style="padding: 6px 8px;">Cookie / LocalStorage</td>
                <td style="padding: 6px 8px;"><span style="background: #ede9fe; color: #6b21a8; padding: 2px 6px; border-radius: 4px;">${data.catBadgeMedia || 'Media'}</span></td>
                <td style="padding: 6px 8px;">${data.ytDuration || 'Session to 2 years'}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="pdf-section" style="border-bottom: 1px solid #e2e8f0; padding-bottom: 12px; margin-bottom: 6px;">
          <h2 style="font-size: 13.5px; font-weight: 800; color: #0f172a; margin: 0 0 6px;">4. ${data.sec4Title || ''}</h2>
          <p style="font-size: 11px; line-height: 1.6; color: #334155; margin: 0 0 6px;">${data.sec4P1 || ''}</p>
          <p style="font-size: 11px; line-height: 1.6; color: #334155; margin: 0;">${data.sec4P2 || ''}</p>
        </div>

        <div class="pdf-section">
          <h2 style="font-size: 13.5px; font-weight: 800; color: #0f172a; margin: 0 0 6px;">5. ${data.sec5Title || ''}</h2>
          <p style="font-size: 11px; line-height: 1.6; color: #334155; margin: 0 0 6px;">${data.sec5P1 || ''}</p>
          <div style="font-size: 10.5px; line-height: 1.5; color: #475569;">
            <strong>${data.companyName || 'JALUD Embedded s.r.o.'}</strong><br>
            ${data.companyAddress || 'Nepomucká 1355/261, 326 00 Plzeň, Czech Republic'}<br>
            ${data.companyId || 'IČO: 03056287'}<br>
            Email: info@jalud-embedded.com &bull; Web: www.jalud.eu
          </div>
        </div>
      </div>
    `;
  } else {
    const sections = data.sections || [];
    bodyHtml = `
      <div style="display: flex; flex-direction: column; gap: 14px;">
        ${sections.map(sec => `
          <div class="pdf-section" style="border-bottom: 1px solid #e2e8f0; padding-bottom: 12px; margin-bottom: 4px;">
            <h2 style="font-size: 13px; font-weight: 800; color: #0f172a; margin: 0 0 6px; display: flex; align-items: center; gap: 8px;">
              <span style="background: #e0f2fe; color: #0284c7; width: 22px; height: 22px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 800; flex-shrink: 0;">
                ${sec.num}
              </span>
              <span>${sec.title}</span>
            </h2>
            <div style="font-size: 11px; line-height: 1.6; color: #334155;">
              ${sec.text}
            </div>
          </div>
        `).join('')}
      </div>
    `;
  }

  return `
    <div id="sed-pdf-root" style="width: 744px; padding: 20px 24px; background: #ffffff; color: #0f172a; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; box-sizing: border-box; direction: ${isRtl ? 'rtl' : 'ltr'}; text-align: ${isRtl ? 'right' : 'left'};">
      
      <!-- Clean Executive Header on Pure White with Official JALUD Logo -->
      <div class="pdf-header" style="background: #ffffff; padding: 4px 0 16px 0; display: flex; justify-content: space-between; align-items: center; gap: 16px; margin-bottom: 20px; border-bottom: 2px solid #0066cc;">
        <div style="display: flex; align-items: center;">
          <img src="${JALUD_LOGO_BASE64}" alt="JALUD EMBEDDED" style="height: 48px; width: auto; max-width: 220px; object-fit: contain; display: block;" />
        </div>
        <div style="font-size: 10px; line-height: 1.45; color: #475569; text-align: ${isRtl ? 'left' : 'right'};">
          <strong style="color: #0f172a; font-size: 12.5px; font-weight: 800; letter-spacing: 0.2px;">JALUD Embedded s.r.o.</strong><br>
          Nepomucká 1355/261, 326 00 Plzeň, Czech Republic<br>
          IČO: 03056287 &bull; DIČ: CZ03056287<br>
          <span style="color: #0066cc; font-weight: 600;">www.jalud.eu</span> &bull; info@jalud-embedded.com
        </div>
      </div>

      <!-- Document Title & Header Meta -->
      <div class="pdf-section" style="margin-bottom: 16px;">
        <div style="font-size: 10px; font-weight: 800; color: #0066cc; text-transform: uppercase; letter-spacing: 0.8px; margin-bottom: 4px;">
          ${data.eyebrow || 'LEGAL DOCUMENTATION & COMPLIANCE'}
        </div>
        <h1 style="font-size: 19px; font-weight: 800; color: #0f172a; margin: 0 0 6px; line-height: 1.25;">
          ${data.title}
        </h1>
        <p style="font-size: 11px; line-height: 1.5; color: #475569; margin: 0 0 12px;">
          ${data.subtitle}
        </p>

        <!-- Meta badges -->
        <div style="display: flex; gap: 8px; align-items: center; flex-wrap: wrap; font-size: 10px;">
          <span style="background: #f1f5f9; border: 1px solid #cbd5e1; padding: 3px 8px; border-radius: 4px; font-weight: 700; color: #0f172a;">
            ${versionText}
          </span>
          <span style="background: #eff6ff; border: 1px solid #bfdbfe; padding: 3px 8px; border-radius: 4px; font-weight: 700; color: #1d4ed8;">
            ${lang.toUpperCase()}
          </span>
          <span style="background: #f8fafc; border: 1px solid #e2e8f0; padding: 3px 8px; border-radius: 4px; color: #64748b;">
            ${labels.officialDoc}
          </span>
        </div>
      </div>

      <!-- Executive Preamble -->
      ${data.preamble ? `
        <div class="pdf-section" style="background: #f8fafc; border-left: ${isRtl ? 'none' : '4px solid #0066cc'}; border-right: ${isRtl ? '4px solid #0066cc' : 'none'}; padding: 10px 14px; border-radius: 4px; margin-bottom: 16px; font-size: 11px; line-height: 1.6; color: #1e293b;">
          ${data.preamble}
        </div>
      ` : ''}

      <!-- Table of Contents -->
      ${data.sections && data.sections.length > 0 ? `
        <div class="pdf-section" style="background: #f0f7ff; border: 1px solid #bae6fd; border-radius: 6px; padding: 12px 16px; margin-bottom: 18px;">
          <div style="font-size: 10.5px; font-weight: 800; color: #0369a1; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 8px;">
            📑 ${data.tocTitle || labels.toc}
          </div>
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 4px 14px; font-size: 10px;">
            ${data.sections.map(s => `
              <div style="display: flex; gap: 6px; color: #0f172a;">
                <span style="font-weight: 800; color: #0284c7; min-width: 16px;">${s.num}.</span>
                <span style="font-weight: 600;">${s.title}</span>
              </div>
            `).join('')}
          </div>
        </div>
      ` : ''}

      <!-- Document Body -->
      ${bodyHtml}

      <!-- Verification Footer Note -->
      <div class="pdf-section" style="margin-top: 20px; padding: 10px 14px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px; font-size: 9.5px; color: #64748b; display: flex; justify-content: space-between; align-items: center;">
        <div>
          <strong>${labels.officialDoc}</strong> &bull; JALUD Embedded s.r.o.
        </div>
        <div>
          ${labels.generatedOn}: ${today}
        </div>
      </div>
    </div>
  `;
}

/**
 * Main export function: generates and triggers instant PDF download in browser
 */
export async function exportLegalDocumentToPdf(docKey, lang = 'cs', btnElement = null) {
  const normKey = DOC_DATA_MAP[docKey] || docKey;
  const docGroup = legalTranslations[normKey];
  
  if (!docGroup) {
    console.error(`[PDF Export] Document group not found for key: ${docKey}`);
    return;
  }

  const data = docGroup[lang] || docGroup['en'] || docGroup['cs'];
  if (!data) {
    console.error(`[PDF Export] Language data not found for ${normKey}/${lang}`);
    return;
  }

  const isRtl = lang === 'ar';
  const labels = I18N_LABELS[lang] || I18N_LABELS.en;
  let originalBtnHtml = '';

  // Show visual loading state on button
  if (btnElement) {
    originalBtnHtml = btnElement.innerHTML;
    btnElement.disabled = true;
    btnElement.innerHTML = `
      <span style="display: inline-flex; align-items: center; gap: 6px;">
        <svg style="animation: sed-spin 1s linear infinite;" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
          <circle cx="12" cy="12" r="10" stroke-opacity="0.25"></circle>
          <path d="M12 2a10 10 0 0 1 10 10" stroke-linecap="round"></path>
        </svg>
        <span>${labels.loading}</span>
      </span>
    `;
  }

  // Create isolated off-screen rendering element behind page
  const tempContainer = document.createElement('div');
  tempContainer.style.position = 'fixed';
  tempContainer.style.left = '0';
  tempContainer.style.top = '0';
  tempContainer.style.width = '744px';
  tempContainer.style.background = '#ffffff';
  tempContainer.style.zIndex = '-99999';
  tempContainer.style.pointerEvents = 'none';
  tempContainer.innerHTML = buildDocumentHtml(normKey, lang, data);
  document.body.appendChild(tempContainer);

  try {
    console.log(`[PDF Export] Starting PDF generation for ${normKey} (${lang})...`);

    if (document.fonts && document.fonts.ready) {
      await document.fonts.ready;
    }
    await new Promise(r => setTimeout(r, 150));

    const renderRoot = tempContainer.querySelector('#sed-pdf-root');

    // Ensure all images (e.g. official logo) are fully decoded and loaded
    const images = Array.from(renderRoot.querySelectorAll('img'));
    await Promise.all(
      images.map(img => {
        if (img.complete) return Promise.resolve();
        return new Promise(resolve => {
          img.onload = resolve;
          img.onerror = resolve;
        });
      })
    );

    const html2canvasFn = typeof html2canvas === 'function' ? html2canvas : (html2canvas && html2canvas.default ? html2canvas.default : html2canvas);
    const fullCanvas = await html2canvasFn(renderRoot, {
      scale: 2, // 2x DPI for ultra-sharp typography
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
      scrollX: 0,
      scrollY: 0
    });

    // Page Geometry: A4 in mm (210mm x 297mm)
    // Root element width in CSS = 744 px. With scale: 2 => fullCanvas.width = 1488 px
    // Left & Right margins = 12 mm each => Printable width = 186 mm
    // Ratio on 2x canvas: 1488 px / 186 mm = 8.0 canvas pixels per mm
    const pxPerMm = 8.0;
    const a4CanvasWidth = 1680; // 210 mm * 8.0 px/mm
    const a4CanvasHeight = 2376; // 297 mm * 8.0 px/mm
    const leftMarginCanvasPx = 96; // 12 mm * 8.0 px/mm
    const printableWidthCanvasPx = 1488; // 186 mm * 8.0 px/mm

    // Max content height per page in CSS pixels (1 CSS px = 2 canvas px => 1 mm = 4 CSS px)
    // Page 1: content top 12mm (48 CSS px), content bottom ends by 281mm (1124 CSS px) => usable = 1076 CSS px
    // Page 2+: content top 17mm (68 CSS px), content bottom ends by 281mm (1124 CSS px) => usable = 1056 CSS px
    const page1MaxContentCssPx = 1076;
    const page2MaxContentCssPx = 1056;

    // Collect DOM section elements to prevent cutting through cards
    const rootRect = renderRoot.getBoundingClientRect();
    const sectionEls = Array.from(renderRoot.querySelectorAll('.pdf-section, .pdf-header'));
    const sectionBounds = sectionEls.map(el => {
      const rect = el.getBoundingClientRect();
      return {
        top: rect.top - rootRect.top,
        bottom: rect.bottom - rootRect.top
      };
    });

    // Total content height in CSS pixels and scale ratio
    const rootHeightCssPx = Math.ceil(renderRoot.offsetHeight || renderRoot.scrollHeight || rootRect.height);
    const scaleRatio = fullCanvas.width / 744;

    // Determine smart page cut points avoiding mid-section splits
    const pageCuts = [];
    let currentY = 0;
    let pageIndex = 0;

    while (currentY < rootHeightCssPx) {
      const maxContentPx = (pageIndex === 0) ? page1MaxContentCssPx : page2MaxContentCssPx;
      let targetY = currentY + maxContentPx;

      if (targetY >= rootHeightCssPx - 20) {
        pageCuts.push({ startY: currentY, endY: rootHeightCssPx, isFirstPage: pageIndex === 0 });
        break;
      }

      // Check if any section intersects targetY
      let cutY = targetY;
      for (const b of sectionBounds) {
        if (b.top < targetY && b.bottom > targetY) {
          // If cutting before this section still leaves reasonable content on current page (> 25% page)
          if (b.top > currentY + (maxContentPx * 0.25)) {
            cutY = b.top;
          }
          break;
        }
      }

      pageCuts.push({ startY: currentY, endY: cutY, isFirstPage: pageIndex === 0 });
      currentY = cutY;
      pageIndex++;
    }

    const totalPages = pageCuts.length;
    const pdf = createPdfInstance('p', 'mm', 'a4');

    // Render each page with guaranteed margins, headers, and footers directly on Canvas
    // This completely eliminates all jsPDF text font encoding issues and text collisions
    for (let i = 0; i < totalPages; i++) {
      const cut = pageCuts[i];
      const sliceHeightCssPx = cut.endY - cut.startY;
      const sliceHeightCanvasPx = Math.round(sliceHeightCssPx * scaleRatio);

      // Create full A4 canvas for this page
      const pageCanvas = document.createElement('canvas');
      pageCanvas.width = a4CanvasWidth;
      pageCanvas.height = a4CanvasHeight;
      const ctx = pageCanvas.getContext('2d');

      // 1. Pure crisp white background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, a4CanvasWidth, a4CanvasHeight);

      // 2. Determine top offset for content slice
      // Page 1: 12mm (96px). Page 2+: 17mm (136px) leaving 5mm clear space below header line (96px)
      const topOffsetCanvasPx = (i === 0) ? 96 : 136;

      // 3. Draw content slice safely onto page canvas
      ctx.drawImage(
        fullCanvas,
        0,
        Math.round(cut.startY * scaleRatio),
        fullCanvas.width,
        sliceHeightCanvasPx,
        leftMarginCanvasPx,
        topOffsetCanvasPx,
        printableWidthCanvasPx,
        sliceHeightCanvasPx
      );

      // 4. Running Header on pages 2+ (Rendered with browser font engine at crisp 8pt)
      if (i > 0) {
        const headerTextY = 68; // 8.5 mm * 8 px/mm
        const headerLineY = 96; // 12 mm * 8 px/mm
        const headerFontSize = 21; // 21px on 2x canvas = 10.5px CSS = ~8pt font

        ctx.font = `600 ${headerFontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif`;
        ctx.fillStyle = '#64748b';
        ctx.textBaseline = 'middle';

        // Right side: short version badge (e.g. "Verze 2026.1" or "Version 2026.1")
        const rightText = (data.version && data.version.includes('2026')) ? 'Verze 2026.1' : (data.version || 'Version 2026.1');
        const rightWidth = ctx.measureText(rightText).width;
        const rightX = a4CanvasWidth - leftMarginCanvasPx;

        // Position version
        ctx.textAlign = isRtl ? 'left' : 'right';
        ctx.fillText(rightText, isRtl ? leftMarginCanvasPx : rightX, headerTextY);

        // Position document title with safety truncation so it NEVER overlaps
        ctx.textAlign = isRtl ? 'right' : 'left';
        const leftX = isRtl ? rightX : leftMarginCanvasPx;
        const maxTitleWidth = printableWidthCanvasPx - rightWidth - 80;

        let leftText = `Sound Event Detector • ${data.title}`;
        if (ctx.measureText(leftText).width > maxTitleWidth) {
          while (ctx.measureText(leftText + '...').width > maxTitleWidth && leftText.length > 10) {
            leftText = leftText.slice(0, -1);
          }
          leftText += '...';
        }
        ctx.fillText(leftText, leftX, headerTextY);

        // Separator rule (17mm / 136px content top offset leaves 5mm clear space below this line)
        ctx.strokeStyle = '#cbd5e1';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(leftMarginCanvasPx, headerLineY);
        ctx.lineTo(a4CanvasWidth - leftMarginCanvasPx, headerLineY);
        ctx.stroke();
      }

      // 5. Running Footer on ALL pages (Rendered with browser font engine at crisp 7.5pt)
      const footerLineY = 2280; // 285 mm * 8 px/mm
      const footerTextY = 2320; // 290 mm * 8 px/mm
      const footerFontSize = 19; // 19px on 2x canvas = 9.5px CSS = ~7.5pt font

      // Separator rule
      ctx.strokeStyle = '#cbd5e1';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(leftMarginCanvasPx, footerLineY);
      ctx.lineTo(a4CanvasWidth - leftMarginCanvasPx, footerLineY);
      ctx.stroke();

      ctx.font = `400 ${footerFontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif`;
      ctx.fillStyle = '#94a3b8';
      ctx.textBaseline = 'middle';

      // Left footer text
      ctx.textAlign = isRtl ? 'right' : 'left';
      ctx.fillText(`${labels.companyNotice} • www.jalud.eu`, isRtl ? (a4CanvasWidth - leftMarginCanvasPx) : leftMarginCanvasPx, footerTextY);

      // Right footer text (Page X / Y)
      ctx.textAlign = isRtl ? 'left' : 'right';
      ctx.fillText(`${labels.pageOf} ${i + 1} / ${totalPages}`, isRtl ? leftMarginCanvasPx : (a4CanvasWidth - leftMarginCanvasPx), footerTextY);

      // 6. Draw to jsPDF as full A4 page (Zero jsPDF text calls = Zero character encoding errors)
      const pageDataUrl = pageCanvas.toDataURL('image/jpeg', 0.95);
      if (i > 0) {
        pdf.addPage();
      }
      pdf.addImage(pageDataUrl, 'JPEG', 0, 0, 210, 297);
    }

    const safeDocName = normKey.replace(/[^a-zA-Z0-9_-]/g, '_').toUpperCase();
    const fileName = `JALUD_${safeDocName}_${lang.toUpperCase()}.pdf`;
    console.log(`[PDF Export] Generated ${totalPages} page(s). Saving ${fileName}...`);
    pdf.save(fileName);
    console.log(`[PDF Export] Successfully exported and triggered download for ${fileName}`);

    if (btnElement) {
      btnElement.innerHTML = `<span>${labels.downloaded}</span>`;
      setTimeout(() => {
        btnElement.disabled = false;
        btnElement.innerHTML = originalBtnHtml;
      }, 2500);
    }
  } catch (err) {
    console.error('[PDF Export] Failed to generate PDF:', err);
    if (btnElement) {
      btnElement.disabled = false;
      btnElement.innerHTML = originalBtnHtml;
    }
  } finally {
    if (tempContainer.parentNode) {
      tempContainer.parentNode.removeChild(tempContainer);
    }
  }
}
